import { View, Text } from '@react-pdf/renderer';
import dayjs from 'dayjs';
import { UsageProduct } from '../../../usage';
import { UsageReport } from '../../../usage/interfaces/usage-report.interface';
import { en } from '../../theme/languages/en';
import { Span } from '../Span/Span';
import { metadataItems } from './UsageLogs.utils';
import { GCell, MONTH_DAY_YEAR, Supplier } from '../../../common';
import { CellTypes } from '../../../pricingModelTable';
import { numberWithCommasFixedNumbers } from '../../utils';
import { styles } from './UsageLogs.module';
import { PageComponent } from '..';

interface UsageLogsProps {
  usageReportsByProduct: UsageProduct[];
  poNumber?: string;
  isSample?: boolean;
  isPreview?: boolean;
  supplier?: Supplier;
}

export const UsageLogs = ({
  usageReportsByProduct,
  poNumber,
  isSample = false,
  isPreview,
  supplier,
}: UsageLogsProps) => {
  const renderCell = (cell: GCell) => {
    switch (cell.cellType) {
      case CellTypes.DATE:
        return cell.cellValue ? dayjs(cell.cellValue).format(MONTH_DAY_YEAR) : '-';
      case CellTypes.NUMBER:
        return typeof cell.cellValue === 'number'
          ? numberWithCommasFixedNumbers(+cell.cellValue)
          : typeof cell.cellValue === 'number'
          ? cell.cellValue
          : '-';
      case CellTypes.TEXT:
        return typeof cell.cellValue === 'number' ? cell.cellValue : cell.cellValue || '-';
    }
  };

  return (
    <>
      {usageReportsByProduct?.map((usageProduct: UsageProduct) => {
        return usageProduct?.usageReports?.map((usageReport: UsageReport) => {
          const tableRows = usageReport?.usageLogsTable.rows;
          const numberOfRows = 30;
          const usageReportRows = [];
          if (isPreview && tableRows.length > 11) {
            usageReportRows.push(tableRows.slice(0, 10));
          } else {
            for (let i = 0; i < tableRows.length; i += numberOfRows) {
              usageReportRows.push(tableRows.slice(i, i + numberOfRows));
            }
          }
          return usageReportRows.map((rows, index) => (
            <PageComponent key={usageReport?.id} supplier={supplier} isPreview={isPreview} isSample={isSample}>
              <View style={styles.container}>
                {index == 0 && (
                  <>
                    <View style={styles.titleContainer}>
                      <Span style={styles.poNumber} canBeMissingValue missingValue={poNumber}>
                        {poNumber ? poNumber : ''}
                      </Span>
                      {isSample && <Span style={styles.sample}>{en.sample}</Span>}
                      <Span style={styles.header}>{en.usageReport}</Span>
                    </View>
                    <View style={styles.flexRow}>
                      <Span canBeMissingValue missingValue={usageProduct?.name} style={styles.title}>
                        {usageProduct?.name}
                      </Span>
                    </View>

                    <View style={styles.metadataContainer}>
                      {metadataItems(usageReport).map(
                        (metadataItem: { key: string; value: any }, metaIndex: number) => (
                          <View
                            style={
                              metaIndex == 0
                                ? styles.firstMetadata
                                : metaIndex == 2
                                ? styles.lastMetadata
                                : styles.metadataItemContainer
                            }
                            key={metaIndex}
                          >
                            <Span key={metaIndex} style={styles.metadataTitleItem}>
                              {metadataItem.key}
                            </Span>
                            <Text key={metaIndex} style={styles.metadataItem}>
                              {metadataItem?.value}
                            </Text>
                          </View>
                        ),
                      )}
                    </View>
                  </>
                )}

                <View style={styles.headerItems} fixed>
                  {usageReport?.usageLogsTable.columns.map((column, colsIndex) => (
                    <Text key={colsIndex} style={[styles.headerItem]}>
                      {column.title}
                    </Text>
                  ))}
                </View>

                <View style={styles.flexColumn}>
                  {rows.map((row, rowIndex) => (
                    <View key={rowIndex} style={styles.row} wrap={false}>
                      {usageReport?.usageLogsTable.columns?.map((column, rowColindex) => (
                        <Text key={`${rowColindex}_${column.title}`} wrap={false} style={styles.cell}>
                          {renderCell(row.cells[column.title])}
                        </Text>
                      ))}
                    </View>
                  ))}
                  {/* for preview with logs over 12 rows */}
                  {isPreview && usageReport?.usageLogsTable.rows.length > 11 && (
                    <View style={styles.additionalLogsContainer} wrap={false}>
                      <Text wrap style={styles.additionalLogs}>
                        + {usageReport?.usageLogsTable.rows.length - 10} {en.additionalLogsForThisProduct}
                      </Text>
                    </View>
                  )}
                </View>
              </View>
            </PageComponent>
          ));
        });
      })}
    </>
  );
};
